import type { SceneValidationStatus, SceneValidationWarning } from '@scoutaround/script-breakdown-shared';

import type { SceneValidationRow } from '../../lib/api.js';
import type { WorkState } from '../ui/StatusPill.js';

/**
 * The four states, in the words the screen uses. A detected scene nobody has
 * looked at is "Not reviewed" — never "confirmed" and never "ready", because
 * being found by the parser says nothing about being right.
 */
export const STATUS_LABEL: Record<SceneValidationStatus, string> = {
  TO_REVIEW: 'Not reviewed',
  CONFIRMED: 'Confirmed',
  NEEDS_ATTENTION: 'Needs attention',
  EXCLUDED: 'Excluded',
};

/** The same four states in the workspace's one status vocabulary (its colours). */
export const STATUS_STATE: Record<SceneValidationStatus, WorkState> = {
  TO_REVIEW: 'READY_FOR_REVIEW',
  CONFIRMED: 'CONFIRMED',
  NEEDS_ATTENTION: 'NEEDS_ATTENTION',
  EXCLUDED: 'EXCLUDED',
};

/** The short mark a dense row carries for interior / exterior. */
export const INT_EXT_SHORT: Record<string, string> = {
  INT: 'INT',
  EXT: 'EXT',
  INT_EXT: 'I/E',
};

export const INT_EXT_LABEL: Record<string, string> = {
  INT: 'Interior',
  EXT: 'Exterior',
  INT_EXT: 'Int./Ext.',
};

/** A scene's own number, or its place in the screenplay when it has none. */
export function sceneLabel(scene: Pick<SceneValidationRow, 'sceneNumber' | 'ordinal'>): string {
  return scene.sceneNumber ?? String(scene.ordinal);
}

/**
 * Each warning as a title and the one thing to do about it. The remedy names a
 * control on the panel, so "needs attention" is never a dead end.
 */
export function warningText(
  warning: SceneValidationWarning,
  scene: SceneValidationRow,
): { title: string; body: string } {
  switch (warning) {
    case 'NO_TEXT':
      return scene.origin === 'MANUAL'
        ? {
            title: 'No scene text yet',
            body: 'Type or paste what happens in this scene. Without it, the AI breakdown has only the heading to read.',
          }
        : { title: 'No text under the heading', body: 'The parser found nothing under this heading. Exclude it if it is not a scene.' };
    case 'HEADING_ONLY':
      return {
        title: 'Only a heading',
        body: 'No action or dialogue under it. It may be an establishing shot — or a line the parser took for a heading. Exclude it, or merge it into the next scene, if it is not a scene of its own.',
      };
    case 'HEADING_NOT_RECOGNISED':
      return {
        title: 'INT./EXT. not stated',
        body: 'The heading does not say interior or exterior. Set it below if it should.',
      };
    case 'POSSIBLE_MISSED_HEADING':
      return {
        title: 'A line inside reads like a scene heading',
        body: 'Two scenes may be run together. If a new scene starts at that line, split the scene there.',
      };
    case 'DUPLICATE_SCENE_NUMBER':
      return {
        title: 'Scene number used twice',
        body: `Another scene is also numbered ${scene.sceneNumber ?? ''}. Correct the number if this one is wrong.`,
      };
  }
}

/** Line starts of a text, with each line — what the split control offers. */
export function linesWithOffsets(text: string): { offset: number; line: string }[] {
  const lines: { offset: number; line: string }[] = [];
  let offset = 0;

  for (const line of text.split('\n')) {
    lines.push({ offset, line });
    offset += line.length + 1;
  }

  return lines;
}
